// slashItems — the `/` menu's projection of the per-template EditorShape.
//
// Promoted from sketches/editor/slashItems.ts (document-first-editor.md §2.2).
// The one idea it makes tangible: the slash menu is NOT a fixed Notion list —
// it is the SAME `EditorShape` the Palette already reads, re-projected into a
// keyboard popup. Feed it the quiz template's Shape and only question-shaped
// rows appear; feed it the rich Shape and every block + media kind shows. There
// is no per-template `if` here — the allow-list IS the menu.
//
// The rows show in the one Create menu (CreateMenu.tsx, pen "Studio · Create
// menu"): a heading is one chip per level (H1-H3) in its Headings row, every
// other kind a row under Insert.
//
// Discipline mirrors Palette.tsx: kind → author-facing metadata is a TOTAL
// switch on the discriminant (no cast; a new kind stops it compiling until
// named), and the pure switch returns `msg` MessageDescriptors, localised at
// the impure edge (`resolveSlashItems`), never a hand-authored string.

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
    type CreateMenuGroup,
    type CreateMenuRow,
    createMenuGroups,
} from "../../CreateMenu/CreateMenu.js";
import type { IconName } from "../../Icon/Icon.js";
import type {
    EditorShape,
    InsertIntent,
    MediaKind,
} from "../Palette/Palette.js";
import type { Element, ElementKind } from "../types.js";

// A row the surface around the editor adds (the Studio's Ask here, Create
// from a source, Insert a part): the editor hands back its place in the
// surface's own list, and the surface knows what it means. `group` is where
// in the Create menu it stands; `more`, that it opens a next step; `asks`,
// that it takes the words typed after "/" as a request (Ask here: its row
// shows them).
type SurfaceSlashIntent = {
    readonly type: "surface";
    readonly at: number;
    readonly group: Extract<CreateMenuGroup, "top" | "insert">;
    readonly more: boolean;
    readonly asks: boolean;
};

// A heading at one level: one chip each in the menu's Headings row.
type HeadingLevel = Extract<Element, { type: "heading" }>["level"];
type HeadingSlashIntent = {
    readonly type: "heading";
    readonly level: HeadingLevel;
};
const headingLevels: readonly HeadingLevel[] = [1, 2, 3, 4, 5, 6];

// The author-facing shape of one slash row, already localised — what the menu
// renders and reports up. `keywords` are extra filter tokens ("h1", "question")
// so `/quest` can find the MCQ row even though its label is "Multiple-choice
// question"; matching runs over label + keywords (matchSlash below).
export type SlashItem = {
    readonly intent: InsertIntent | SurfaceSlashIntent | HeadingSlashIntent;
    // What the row shows ("H2" on a chip) and its accessible name
    // ("Heading 2"); the same words on every other row.
    readonly label: string;
    readonly name: string;
    readonly hint: string;
    readonly icon: IconName;
    readonly keywords: readonly string[];
};

// The pre-localisation spec: descriptors + icon + keywords, no i18n bound yet.
// A projection of the Shape, in the Shape's own order — the single source.
type SlashSpec = {
    readonly intent: InsertIntent | HeadingSlashIntent;
    readonly label: MessageDescriptor;
    readonly name: MessageDescriptor;
    readonly hint: MessageDescriptor;
    readonly icon: IconName;
    readonly keywords: readonly string[];
};

type KindSpec = Omit<SlashSpec, "intent" | "name">;

// kind → metadata, total over the block kinds a row inserts (a heading is
// its chips, headingSpec below). Same words as Palette's blockMeta; keywords
// add the shortcuts an author types ("para", "quiz").
const blockSpec = (kind: Exclude<ElementKind, "heading">): KindSpec => {
    switch (kind) {
        case "prose":
            return {
                label: msg`Paragraph`,
                hint: msg`A block of body text`,
                icon: "paragraph",
                keywords: ["text", "para", "body"],
            };
        case "objective":
            return {
                label: msg`Learning objective`,
                hint: msg`What the learner should be able to do`,
                icon: "objective",
                keywords: ["goal", "outcome", "list"],
            };
        case "note":
            return {
                label: msg({ context: "block kind", message: "Note" }),
                hint: msg`Only you and the assistant see it`,
                icon: "note",
                keywords: ["note", "memo", "private", "anteckning"],
            };
        case "mcq":
            return {
                label: msg`Question`,
                hint: msg`A question with selectable answers`,
                icon: "help",
                keywords: ["question", "multiple choice", "mcq", "answer"],
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// One chip per level: "H2" on the chip, "Heading 2" its name; "/h2" or
// "/heading" finds it.
const headingSpec = (level: HeadingLevel): SlashSpec => {
    return {
        intent: { type: "heading", level },
        label: msg`H${level}`,
        name: msg`Heading ${level}`,
        hint: msg`A section title`,
        icon: "heading",
        keywords: [`h${level}`, "heading", "title", "section"],
    };
};

// kind → metadata, total over MediaKind. The Shape gates these too, so a quiz
// Shape with an empty `mediaKinds` yields no media rows — for free.
const mediaSpec = (kind: MediaKind): KindSpec => {
    switch (kind) {
        case "image":
            return {
                label: msg`Image`,
                hint: msg`A picture with alt text`,
                icon: "image",
                keywords: ["picture", "photo", "img"],
            };
        case "video":
            return {
                label: msg`Video`,
                hint: msg`A video clip with captions`,
                icon: "video",
                keywords: ["clip", "movie"],
            };
        case "audio":
            return {
                label: msg`Audio`,
                hint: msg`A sound clip with a transcript`,
                icon: "audio",
                keywords: ["sound", "voice"],
            };
        case "embed":
            return {
                label: msg`Embed`,
                hint: msg`An external interactive frame`,
                icon: "embed",
                keywords: ["iframe", "external", "widget"],
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// Which of the Shape's rows a surface keeps beside its own: every row (no
// surface), or only the headings (the Studio, whose own rows hold its
// Insert group).
export type SlashKeep = "all" | "headings";

// Project the Shape into ordered specs — headings first (when the Shape has
// them), then the other blocks, then media, preserving each allow-list's own
// order. Pure: Shape in, specs out, no i18n, no state.
const slashSpecs = (
    shape: EditorShape,
    keep: SlashKeep,
): readonly SlashSpec[] => [
    ...(shape.rootKinds.includes("heading")
        ? headingLevels.map(headingSpec)
        : []),
    ...(keep === "all" ? shape.rootKinds : []).flatMap(
        (kind): readonly SlashSpec[] =>
            kind === "heading"
                ? []
                : [
                      {
                          intent: { type: "block", kind },
                          ...blockSpec(kind),
                          name: blockSpec(kind).label,
                      },
                  ],
    ),
    ...(keep === "all" ? shape.mediaKinds : []).map(
        (kind): SlashSpec => ({
            intent: { type: "media", kind },
            ...mediaSpec(kind),
            name: mediaSpec(kind).label,
        }),
    ),
];

// One block row, localised: a surface that lists its own rows can still
// insert the editor's block (the Studio's Question).
export const blockSlashItem = (
    kind: Exclude<ElementKind, "heading">,
    i18n: I18n,
): SlashItem => {
    const spec = blockSpec(kind);
    return {
        intent: { type: "block", kind },
        label: i18n._(spec.label),
        name: i18n._(spec.label),
        hint: i18n._(spec.hint),
        icon: spec.icon,
        keywords: spec.keywords,
    };
};

// Localise the specs at the impure edge (the component has `useLingui`), turning
// descriptors into the strings the menu renders and filters on.
export const resolveSlashItems = (
    shape: EditorShape,
    i18n: I18n,
    keep: SlashKeep = "all",
): readonly SlashItem[] =>
    slashSpecs(shape, keep).map((spec) => ({
        intent: spec.intent,
        label: i18n._(spec.label),
        name: i18n._(spec.name),
        hint: i18n._(spec.hint),
        icon: spec.icon,
        keywords: spec.keywords,
    }));

// Where a row stands in the Create menu.
export const slashGroupOf = (item: SlashItem): CreateMenuGroup => {
    switch (item.intent.type) {
        case "surface":
            return item.intent.group;
        case "heading":
            return "headings";
        case "block":
        case "media":
            return "insert";
        default: {
            const exhaustive: never = item.intent;
            return exhaustive;
        }
    }
};

// A row as the Create menu shows it: the top rows say what they do on a
// line under the name; under Insert only the Note does (who sees it, pen
// Q73), the other parts say it by their name.
export const menuRowOf = (item: SlashItem): CreateMenuRow => {
    const group = slashGroupOf(item);
    const line =
        group === "top" ||
        (item.intent.type === "block" && item.intent.kind === "note");
    return {
        key: slashRowKey(item),
        group,
        label: item.label,
        name: item.name,
        ...(line ? { hint: item.hint } : {}),
        icon: item.icon,
        more: item.intent.type === "surface" && item.intent.more,
    };
};

// The key of a row, stable across re-filters: the intent's discriminant and
// kind, level or (for a surface row) place.
const slashRowKey = (item: SlashItem): string => {
    switch (item.intent.type) {
        case "surface":
            return `surface:${item.intent.at}`;
        case "heading":
            return `heading:${item.intent.level}`;
        case "block":
        case "media":
            return `${item.intent.type}:${item.intent.kind}`;
        default: {
            const exhaustive: never = item.intent;
            return exhaustive;
        }
    }
};

// The rows in the menu's order (its groups, top to bottom; each group in
// the order given), so the keyboard walks them as the eye reads them.
export const inMenuOrder = (
    items: readonly SlashItem[],
): readonly SlashItem[] =>
    createMenuGroups.flatMap((group) =>
        items.filter((item) => slashGroupOf(item) === group),
    );

// Filter by the text typed after `/`. Empty query ⇒ the whole (Shape-narrowed)
// list. Otherwise keep the top rows (Ask here, Create from a source…: they
// take whatever was typed, the default case) and the rows whose label, name
// or a keyword contains the query — pure, case-insensitive, order-preserving.
// This is the "type to narrow" channel.
export const matchSlash = (
    items: readonly SlashItem[],
    query: string,
): readonly SlashItem[] => {
    const q = query.trim().toLowerCase();
    if (q === "") return items;
    return items.filter(
        (item) =>
            slashGroupOf(item) === "top" ||
            item.label.toLowerCase().includes(q) ||
            item.name.toLowerCase().includes(q) ||
            item.keywords.some((k) => k.includes(q)),
    );
};

// Whether a row takes the typed words as a request (Ask here).
export const slashAsks = (item: SlashItem): boolean =>
    item.intent.type === "surface" && item.intent.asks;

// The rows for what was typed after `/`, as the menu shows them: matched
// (matchSlash), and a row that asks shows the words as its second line.
export const slashRowsFor = (
    items: readonly SlashItem[],
    query: string,
): readonly SlashItem[] => {
    const words = query.trim();
    return matchSlash(items, query).map((item) =>
        words !== "" && slashAsks(item) ? { ...item, hint: words } : item,
    );
};

// Where the keyboard cursor starts: on a part or heading the words name
// exactly ("/lesson", "/h2"), else on the first row (Ask here at the
// Studio's caret: anything else lands there).
export const slashStartAt = (
    rows: readonly SlashItem[],
    query: string,
): number => {
    const q = query.trim().toLowerCase();
    const named = rows.findIndex(
        (item) =>
            slashGroupOf(item) !== "top" &&
            (item.label.toLowerCase() === q ||
                item.name.toLowerCase() === q ||
                item.keywords.includes(q)),
    );
    return q === "" || named < 0 ? 0 : named;
};
